import {Collapse} from 'bootstrap';
import Isotope from 'isotope-layout';
import Helper from './helper';

import.meta.glob([
    '../img/**',
    '../video/**'
]);

class App {
    constructor() {
        this.init();
    }

    init() {
        this.initStickyNavigation();
        this.initSearchNavigation();
        this.fileUploads();
        this.asyncForms();
        this.cookies();
        this.isotope();
        this.stepsScroller();
        this.scrollIntoView();
        this.responsiveEmbeds();
    }

    initStickyNavigation() {
        const navbar = document.getElementById('navigation');

        const header = document.querySelector('header');
        const toggler = navbar?.querySelector('.navbar-toggler');
        const collapse = navbar?.querySelector('#main-navbar-content');

        if (navbar === null || header == null) {
            return false;
        }

        const offset = 1;

        let wasCompact = false;

        const closeCompactMenu = () => {
            if (toggler === null || collapse === null) {
                return;
            }

            toggler.classList.add('collapsed');
            toggler.setAttribute('aria-expanded', 'false');
            collapse.classList.remove('show');
        };

        window.addEventListener('scroll', () => {
            const isCompact = window.pageYOffset >= offset;

            if (isCompact) {
                navbar.classList.add('sticky');
            } else {
                navbar.classList.remove('sticky');
            }

            if (isCompact && wasCompact === false && window.innerWidth >= 992) {
                closeCompactMenu();
            }

            wasCompact = isCompact;
        });
    }

    initSearchNavigation() {
        const navbar = document.getElementById('navigation');
        const navbarCollapse = document.getElementById('main-navbar-content');
        const searchCollapse = document.getElementById('search-collapse');
        const navbarToggler = navbar?.querySelector('.navbar-toggler');
        const searchToggle = navbar?.querySelector('.navigation__search-toggle');

        if (navbar === null || searchToggle === null || searchCollapse === null) {
            return false;
        }

        searchToggle.removeAttribute('data-bs-toggle');
        searchToggle.removeAttribute('data-bs-target');

        const searchCollapseInstance = Collapse.getOrCreateInstance(searchCollapse, {
            toggle: false
        });

        const updateSearchToggleState = () => {
            const isExpanded = searchCollapse.classList.contains('show');

            searchToggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        };

        searchCollapse.addEventListener('shown.bs.collapse', updateSearchToggleState);
        searchCollapse.addEventListener('hidden.bs.collapse', updateSearchToggleState);

        updateSearchToggleState();

        searchToggle.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const isSearchTransitioning = searchCollapse.classList.contains('collapsing');
            const isNavbarOpen = navbarCollapse?.classList.contains('show') ?? false;

            if (isSearchTransitioning) {
                return;
            }

            if (isNavbarOpen && navbarCollapse !== null) {
                Collapse.getOrCreateInstance(navbarCollapse, {toggle: false}).hide();

                if (navbarToggler !== null) {
                    navbarToggler.classList.add('collapsed');
                    navbarToggler.setAttribute('aria-expanded', 'false');
                }
            }

            searchCollapseInstance.toggle();
        });
    }

    fileUploads() {
        document.querySelectorAll('.form-upload__input').forEach(input => {
            const upload = input.closest('.form-upload');

            const fileName = upload?.querySelector('.form-upload__filename');

            if (upload === null || fileName === null) {
                return;
            }

            const updateFileName = () => {
                const files = Array.from(input.files ?? []);

                const emptyText = fileName.dataset.emptyText ?? '';

                fileName.textContent = files.length > 0
                    ? files.map(file => file.name).join(', ')
                    : emptyText;

                upload.classList.toggle('has-file', files.length > 0);
            };

            updateFileName();

            input.addEventListener('change', updateFileName);

            input.form?.addEventListener('reset', () => {
                window.requestAnimationFrame(updateFileName);
            });
        });
    }

    asyncForms() {
        let recaptchaReadyPromise = null;

        const recaptchaInputs = document.querySelectorAll('.g-recaptcha-response');

        const hasRecaptchaInputs = recaptchaInputs.length > 0;

        const isRecaptchaReady = () => typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.execute === 'function';

        const waitForRecaptcha = () => {
            if (hasRecaptchaInputs === false || isRecaptchaReady()) {
                return Promise.resolve();
            }

            if (recaptchaReadyPromise !== null) {
                return recaptchaReadyPromise;
            }

            recaptchaReadyPromise = new Promise(resolve => {
                let poller = null;
                let attempts = 0;
                const maxAttempts = 100;

                const cleanup = () => {
                    document.removeEventListener('recaptcha:ready', handleReady);

                    if (poller !== null) {
                        window.clearInterval(poller);
                    }

                    resolve();
                };

                const handleReady = () => {
                    cleanup();
                };

                document.addEventListener('recaptcha:ready', handleReady);

                poller = window.setInterval(() => {
                    attempts += 1;

                    if (isRecaptchaReady() || attempts >= maxAttempts) {
                        cleanup();
                    }
                }, 100);
            });

            return recaptchaReadyPromise;
        };

        waitForRecaptcha();

        /**
         * Execute reCAPTCHA
         */
        let executeRecaptcha = async (form) => {
            const recaptchaInput = form.querySelector('.g-recaptcha-response');

            if (recaptchaInput === null) {
                return true;
            }

            await waitForRecaptcha();

            if (isRecaptchaReady() === false) {
                return false;
            }

            return new Promise(resolve => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(config.recaptcha_site_key, {
                        action : config.recaptcha_action
                    }).then(token => {
                        recaptchaInput.value = token;

                        resolve(true);
                    }).catch(() => {
                        resolve(false);
                    });
                });
            });
        };

        document.querySelectorAll('.form--async').forEach(form => {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();

                const recaptchaExecuted = await executeRecaptcha(form);

                if (recaptchaExecuted === false) {
                    alert('Er is iets misgegaan.');

                    return;
                }

                // Serialize all form data
                let formData = new FormData(form);

                // Fetch container for the form messages
                let formMessages = form.querySelector('.form-messages');

                // Clear the form errors
                form.querySelectorAll('[data-error]').forEach(errorMessage => {
                    // Clear error message
                    errorMessage.innerHTML = '';

                    // Remove multiple status class
                    errorMessage.classList.remove('multiple');
                });

                // Clear the form messages
                formMessages.replaceChildren();

                Helper.post(form.getAttribute('action'), formData).then(response => {
                    if (response.redirect_url) {
                        // Redirect to the given URL
                        window.location.href = response.redirect_url;
                    }
                    else {
                        // Reset form
                        form.reset();

                        formMessages.innerHTML = '<div class="alert alert--' + response.message.type + '"><span>' + response.message.text + '</span></div>';
                    }
                })
                .catch(error => {
                    let statusCode = error.statusCode;

                    switch (statusCode) {
                        case 422:
                            // Set error messages
                            Object.entries(error.response.errors).forEach(([key, errors]) => {
                                // Find the target element with data-error attribute
                                const targetElement = form.querySelector('[data-error="' + key + '"]');

                                if (targetElement === null) {
                                    return;
                                }

                                if (errors.length > 1) {
                                    // Mark container as multiple errors
                                    targetElement.classList.add('multiple');
                                }

                                errors.forEach(function (error) {
                                    // Create a new <span> element
                                    const errorSpan = document.createElement('span');

                                    // Set the text content of the <span>
                                    errorSpan.textContent = error;

                                    // Append the <span> as a child of the target element
                                    targetElement.appendChild(errorSpan);
                                });
                            });
                            break;

                        default:
                            // Show notification
                            alert('Er is iets misgegaan.');
                    }

                    // Reject the promise
                    return Promise.reject(error.statusText);
                });
            });
        });
    }

    cookies() {
        let form = document.getElementById('form-cookies');

        if (form === null) {
            return false;
        }

        // Show the cookie form
        form.classList.remove('fade');

        // Add cookie selection state
        document.body.classList.add('cookie-selection');

        let options = form.querySelector('.cookies-banner__options');

        let textChecked   = 'Alle cookies accepteren';
        let textUnchecked = 'Geselecteerde cookies accepteren'

        let buttonSubmit = form.querySelector('button[type="submit"]');

        let isAllCookiesChecked = function () {
            let checked = true;

            options.querySelectorAll('.form-check-input').forEach(switchElement => {
                if (switchElement.checked === false) {
                    checked = false;
                }
            });

            return checked;
        }

        options.querySelectorAll('.form-check-input').forEach(input => {
            // Bind the change event listener to the form check input
            input.addEventListener('change', function () {
                buttonSubmit.innerText = isAllCookiesChecked() ? textChecked : textUnchecked;
            });

            // Manually create on on-change event
            let event = new Event('change');

            // Manually dispatch event
            input.dispatchEvent(event);
        });

        const collapseCookies = document.getElementById('collapse-cookies');

        collapseCookies.addEventListener('show.bs.collapse', () => {
            collapseCookies.querySelectorAll('.form-check-input:not(#cookies-functional)').forEach(input => {
                input.checked = false;
            });

            buttonSubmit.innerText = textUnchecked;
        })

        form.addEventListener('submit', function(e) {
            e.preventDefault();

            let formData = new FormData(this);

            if (e.submitter) {
                // Manually add button name and value
                formData.append(e.submitter.name, e.submitter.value);
            }

            Helper.post(this.action, formData).then(() => {
                // Remove cookie selection state
                document.body.classList.remove('cookie-selection');

                // Fade out the form
                form.classList.add('fade');

                setTimeout(function () {
                    // Reload page
                    location.reload();
                }, 1000);
            })
            .catch(error => {
                // Reject the promise
                return Promise.reject(error.statusText);
            });
        });
    }

    isotope() {
        document.querySelectorAll('.isotope').forEach(container => {
            new Isotope(container, {
                itemSelector: '.isotope-item',
                layoutMode: 'masonry',
                percentPosition: true,
                masonry: {
                    columnWidth: '.isotope-sizer',
                    horizontalOrder: true
                }
            });
        });
    }

    stepsScroller() {
        const scrollers = document.querySelectorAll('.steps__list--scroll');

        if (scrollers.length === 0) {
            return;
        }

        const setScrollerWidth = () => {
            const viewportWidth = document.documentElement.clientWidth;

            scrollers.forEach(scroller => {
                const { left } = scroller.getBoundingClientRect();

                const width = Math.max(0, viewportWidth - left);

                scroller.style.width = `${width}px`;
            });
        };

        const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

        const getScrollStep = scroller => {
            const firstItem = scroller.querySelector('.steps__item');

            if (firstItem === null) {
                return scroller.clientWidth;
            }

            const styles = window.getComputedStyle(scroller);
            const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

            return firstItem.getBoundingClientRect().width + gap;
        };

        const getWheelDelta = (event, scroller) => {
            const rawDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
                ? event.deltaX
                : event.deltaY;

            if (rawDelta === 0) {
                return 0;
            }

            switch (event.deltaMode) {
                case WheelEvent.DOM_DELTA_LINE:
                    return rawDelta * 16;

                case WheelEvent.DOM_DELTA_PAGE:
                    return rawDelta * scroller.clientWidth;

                default:
                    return rawDelta;
            }
        };

        setScrollerWidth();

        window.addEventListener('load', setScrollerWidth);
        window.addEventListener('resize', setScrollerWidth);

        scrollers.forEach(scroller => {
            let isDragging = false;
            let startX = 0;
            let startScrollLeft = 0;
            let wheelScrollTimeout = null;
            const swipeButtons = scroller.parentElement?.querySelectorAll('.steps__swipe') ?? [];

            swipeButtons.forEach(button => {
                button.addEventListener('click', () => {
                    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

                    if (maxScrollLeft <= 0) {
                        return;
                    }

                    const nextScrollLeft = scroller.scrollLeft >= maxScrollLeft - 4
                        ? 0
                        : clamp(scroller.scrollLeft + getScrollStep(scroller), 0, maxScrollLeft);

                    scroller.scrollTo({
                        left: nextScrollLeft,
                        behavior: 'smooth'
                    });
                });
            });

            scroller.addEventListener('mousedown', e => {
                isDragging = true;
                startX = e.pageX;
                startScrollLeft = scroller.scrollLeft;

                scroller.classList.add('is-dragging');
            });

            window.addEventListener('mouseup', () => {
                isDragging = false;
                scroller.classList.remove('is-dragging');
            });

            scroller.addEventListener('mouseleave', () => {
                isDragging = false;
                scroller.classList.remove('is-dragging');
            });

            window.addEventListener('mousemove', e => {
                if (isDragging === false) {
                    return;
                }

                e.preventDefault();

                const distance = e.pageX - startX;

                scroller.scrollLeft = startScrollLeft - distance;
            });

            scroller.addEventListener('dragstart', e => {
                e.preventDefault();
            });

            scroller.addEventListener('wheel', e => {
                const canScrollHorizontally = scroller.scrollWidth > scroller.clientWidth;

                if (canScrollHorizontally === false) {
                    return;
                }

                const horizontalDelta = getWheelDelta(e, scroller);

                if (horizontalDelta === 0) {
                    return;
                }

                e.preventDefault();

                scroller.style.scrollSnapType = 'none';

                if (wheelScrollTimeout !== null) {
                    window.clearTimeout(wheelScrollTimeout);
                }

                const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
                scroller.scrollLeft = clamp(scroller.scrollLeft + horizontalDelta, 0, maxScrollLeft);

                wheelScrollTimeout = window.setTimeout(() => {
                    scroller.style.scrollSnapType = '';
                }, 150);
            }, { passive: false });
        });
    }

    scrollIntoView() {
        document.querySelectorAll('a[href^="#"]').forEach(link => {
            link.addEventListener('click', function (e) {
                const targetId = this.getAttribute('href').substring(1); // remove #

                const targetElement = document.getElementById(targetId);

                if (targetElement) {
                    e.preventDefault(); // stop hash from appearing in URL

                    targetElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });
    }

    responsiveEmbeds() {
        let wrap = function (toWrap, wrapper) {
            wrapper = wrapper || document.createElement('div');
            toWrap.parentNode.insertBefore(wrapper, toWrap);
            return wrapper.appendChild(toWrap);
        }

        // Responsive embeds
        document.querySelectorAll('iframe:not([class])').forEach(iframe => {
            let wrapper = document.createElement('div');

            wrapper.setAttribute('class', 'ratio ratio-16x9');

            wrap(iframe, wrapper);
        });
    }
}

document.addEventListener('DOMContentLoaded', () => new App());
